/**
 * Storage utility functions for accessing localStorage data
 */

import { STORAGE_KEYS } from './constants';

interface LoggedInUserData {
  company_id: string;
  user_id: string;
  email_id: string;
  name: string;
  /** Present when API returns display name separately from login `name` */
  full_name?: string;
  role: string;
  [key: string]: any;
}

/**
 * Retrieves the company_id from the logged-in user data stored in localStorage
 * @returns The company_id string, or empty string if not found or on error
 */
export const getCompanyId = (): string => {
  try {
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    if (storedUserData) {
      const userData: LoggedInUserData = JSON.parse(storedUserData);
      return userData.company_id || '';
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  return '';
};

/**
 * Retrieves the entire logged-in user data from localStorage
 * @returns The user data object, or null if not found or on error
 */
export const getLoggedInUserData = (): LoggedInUserData | null => {
  try {
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    if (storedUserData) {
      return JSON.parse(storedUserData);
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  return null;
};

// Stored value is MY_SUBSCRIPTION API response.data only (plan + subscription fields)
// Matches API response.data: { id, name, planCode, zohoSubscriptionId, zohoCustomerId, subscriptionStatus, endDate, ... }
export interface MySubscriptionStoredData {
  id?: string;
  name?: string;
  planCode?: string;
  description?: string;
  monthlyPrice?: string;
  features?: string[];
  zohoPlanId?: string;
  zohoProductId?: string;
  zohoSubscriptionId?: string | null;
  zoho_subscription_id?: string | null;
  subscription_id?: string | null;
  zohoCustomerId?: string | null;
  zoho_customer_id?: string | null;
  subscriptionStatus?: string;
  endDate?: string;
  cancelAtPeriodEnd?: boolean;
  [key: string]: unknown;
}

/**
 * Retrieves the my subscription data stored in localStorage (MY_SUBSCRIPTION API response.data).
 * Used for current plan label and zohoSubscriptionId for cancel subscription.
 * @returns The stored subscription data object, or null if not found or on error
 */
export const getMySubscriptionData = (): MySubscriptionStoredData | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.MY_SUBSCRIPTION_DATA);
    if (stored) {
      return JSON.parse(stored) as MySubscriptionStoredData;
    }
  } catch (error) {
    console.error('Error parsing my subscription data:', error);
  }
  return null;
};

/**
 * Saves the MY_SUBSCRIPTION API response.data to localStorage.
 * @param data - The response.data object from MY_SUBSCRIPTION API
 */
export const setMySubscriptionData = (data: MySubscriptionStoredData): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.MY_SUBSCRIPTION_DATA, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving my subscription data:', error);
  }
};

/**
 * Gets the Zoho subscription ID from stored my subscription data (for cancel subscription and updateCompanyPlan APIs).
 * Returns empty string when zohoSubscriptionId is null (e.g. free/base plan with no Zoho subscription).
 * @returns zohoSubscriptionId or subscription_id, or empty string if not found or null
 */
export const getZohoSubscriptionId = (): string => {
  const data = getMySubscriptionData();
  if (!data) return '';
  const id = data.zohoSubscriptionId ?? data.zoho_subscription_id ?? data.subscription_id;
  return id && typeof id === 'string' ? id : '';
};

/**
 * Gets the Zoho customer ID from stored my subscription data (for updateCompanyPlan API).
 * @returns zohoCustomerId or zoho_customer_id, or empty string if not found or null
 */
export const getZohoCustomerId = (): string => {
  const data = getMySubscriptionData();
  if (!data) return '';
  const id = data.zohoCustomerId ?? data.zoho_customer_id;
  return id && typeof id === 'string' ? id : '';
};

